import { useState, useEffect } from "react";
import toast from "../../../utils/toast";
import {
  getCommunicationSettings,
  updateCommunicationSettings,
} from "../../../services/propertyOwner/propertyOwnerServices";

export default function MyCommunicationSettings() {
  const [settings, setSettings] = useState({
    smsSource: "SaasApi",
    whatsAppSource: "SaasApi",
    emailSource: "SaasApi",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await getCommunicationSettings();
      if (res.statusCode === 1) {
        setSettings({
          smsSource: res.data.smsSource,
          whatsAppSource: res.data.whatsAppSource,
          emailSource: res.data.emailSource,
        });
      }
    } catch {
      toast.error("Failed to load communication settings");
    } finally {
      setLoading(false);
    }
  };

  // channel = "sms" | "whatsApp" | "email"
  // value   = "SaasApi" | "OwnApi"
  const handleSourceChange = async (channel, value) => {
    const key = `${channel}Source`;
    const prev = settings[key];
    const updated = { ...settings, [key]: value };

    // Optimistic update — show change immediately in UI
    setSettings(updated);
    setSaving((s) => ({ ...s, [channel]: true }));

    try {
      const res = await updateCommunicationSettings(updated);
      if (res.statusCode === 1) {
        toast.success(res.message);
      } else {
        setSettings((s) => ({ ...s, [key]: prev }));
        toast.error(res.message);
      }
    } catch {
      setSettings((s) => ({ ...s, [key]: prev }));
      console.log("Failed to update settings");
    } finally {
      setSaving((s) => ({ ...s, [channel]: false }));
    }
  };

  const badgeLabel = (sourceValue) =>
    sourceValue === "SaasApi" ? "🔌 Using SaaS API" : "⚙ Using Own API";

  if (loading) {
    return (
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              My Communication Settings
            </div>
            <div className="page-sub" id="page-sub">
              API Settings › Choose how SMS, WhatsApp &amp; Email are sent
            </div>
          </div>
        </div>
        <div className="content" id="content-area">
          <div className="loader-container">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              My Communication Settings
            </div>
            <div className="page-sub" id="page-sub">
              API Settings › Choose how SMS, WhatsApp &amp; Email are sent
            </div>
          </div>
          <div className="topbar-right" id="topbar-right" />
        </div>
        <div className="content" id="content-area">
          <div
            className="info-box mb16"
            style={{ background: "var(--blue-light)", color: "#0C447C" }}
          >
            <strong>Pick one source per channel.</strong> If you opt out of a
            channel, NestManager stops sending those messages on your behalf —
            you'll need to configure your own gateway under SMS API, WhatsApp
            API, or Email Setting (links below). Your choice takes effect
            immediately on the Notify pages.
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(360px,1fr))",
              gap: 16,
            }}
          >
            {/* ─── SMS Card ─── */}
            <div className="card" style={{ borderTop: "3px solid #534AB7" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  marginBottom: 14,
                  gap: 10,
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 18, fontWeight: 700, color: "#534AB7" }}
                  >
                    📱 SMS
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginTop: 2,
                    }}
                  >
                    Choose how SMS messages are delivered to your tenants.
                  </div>
                </div>
                <span
                  className="badge badge-info"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {badgeLabel(settings.smsSource)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 0,
                  border: "0.5px solid var(--border-mid)",
                  borderRadius: "var(--radius-md)",
                  overflow: "hidden",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    padding: "12px 14px",
                    background:
                      settings.smsSource === "SaasApi"
                        ? "var(--blue-light)"
                        : "var(--bg-secondary)",
                    borderRight: "0.5px solid var(--border-mid)",
                    cursor: "pointer",
                    opacity: saving.sms ? 0.6 : 1,
                  }}
                  onClick={() => handleSourceChange("sms", "SaasApi")}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="src-sms"
                      checked={settings.smsSource === "SaasApi"}
                      onChange={() => handleSourceChange("sms", "SaasApi")}
                    />
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: "var(--blue)",
                      }}
                    >
                      Use SaaS Admin's SMS API
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      lineHeight: "1.6",
                    }}
                  >
                    ✓ Pre-configured &amp; deliverable on day one
                    <br />
                    ✓ 2,000 SMS included with Growth Plan
                    <br />✓ ₹0.25 per sms after limit
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    padding: "12px 14px",
                    background:
                      settings.smsSource === "OwnApi"
                        ? "var(--blue-light)"
                        : "var(--bg-secondary)",
                    cursor: "pointer",
                    opacity: saving.sms ? 0.6 : 1,
                  }}
                  onClick={() => handleSourceChange("sms", "OwnApi")}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="src-sms"
                      checked={settings.smsSource === "OwnApi"}
                      onChange={() => handleSourceChange("sms", "OwnApi")}
                    />
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: "var(--text-primary)",
                      }}
                    >
                      Use My Own SMS API
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      lineHeight: "1.6",
                    }}
                  >
                    ✓ No SaaS per-message billing
                    <br />
                    ✓ Bring your own gateway credentials
                    <br />⚙{" "}
                    <span
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        color: "var(--blue)",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Configure SMS API →
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--text-tertiary)",
                  background: "var(--bg-secondary)",
                  padding: "8px 12px",
                  borderRadius: "var(--radius-md)",
                }}
              >
                💡 Switch to Own API if you have a high-volume gateway contract
                that beats ₹0.25 per sms.
              </div>
            </div>

            {/* ─── WhatsApp Card ─── */}
            <div className="card" style={{ borderTop: "3px solid #0F6E56" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  marginBottom: 14,
                  gap: 10,
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 18, fontWeight: 700, color: "#0F6E56" }}
                  >
                    💬 WhatsApp
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginTop: 2,
                    }}
                  >
                    Choose how WhatsApp messages are delivered to your tenants.
                  </div>
                </div>
                <span
                  className="badge badge-info"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {badgeLabel(settings.whatsAppSource)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 0,
                  border: "0.5px solid var(--border-mid)",
                  borderRadius: "var(--radius-md)",
                  overflow: "hidden",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    padding: "12px 14px",
                    background:
                      settings.whatsAppSource === "SaasApi"
                        ? "var(--blue-light)"
                        : "var(--bg-secondary)",
                    borderRight: "0.5px solid var(--border-mid)",
                    cursor: "pointer",
                    opacity: saving.whatsApp ? 0.6 : 1,
                  }}
                  onClick={() => handleSourceChange("whatsApp", "SaasApi")}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="src-whatsapp"
                      checked={settings.whatsAppSource === "SaasApi"}
                      onChange={() => handleSourceChange("whatsApp", "SaasApi")}
                    />
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: "var(--blue)",
                      }}
                    >
                      Use SaaS Admin's WhatsApp API
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      lineHeight: "1.6",
                    }}
                  >
                    ✓ Pre-configured &amp; deliverable on day one
                    <br />
                    ✓ 5,000 WhatsApp included with Growth Plan
                    <br />✓ ₹0.50 per whatsapp after limit
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    padding: "12px 14px",
                    background:
                      settings.whatsAppSource === "OwnApi"
                        ? "var(--blue-light)"
                        : "var(--bg-secondary)",
                    cursor: "pointer",
                    opacity: saving.whatsApp ? 0.6 : 1,
                  }}
                  onClick={() => handleSourceChange("whatsApp", "OwnApi")}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="src-whatsapp"
                      checked={settings.whatsAppSource === "OwnApi"}
                      onChange={() => handleSourceChange("whatsApp", "OwnApi")}
                    />
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: "var(--text-primary)",
                      }}
                    >
                      Use My Own WhatsApp API
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      lineHeight: "1.6",
                    }}
                  >
                    ✓ No SaaS per-message billing
                    <br />
                    ✓ Bring your own gateway credentials
                    <br />⚙{" "}
                    <span
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        color: "var(--blue)",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Configure WhatsApp API →
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--text-tertiary)",
                  background: "var(--bg-secondary)",
                  padding: "8px 12px",
                  borderRadius: "var(--radius-md)",
                }}
              >
                💡 Switch to Own API if you have a high-volume gateway contract
                that beats ₹0.50 per whatsapp.
              </div>
            </div>

            {/* ─── Email Card ─── */}
            <div className="card" style={{ borderTop: "3px solid #185FA5" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  marginBottom: 14,
                  gap: 10,
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 18, fontWeight: 700, color: "#185FA5" }}
                  >
                    📧 Email
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginTop: 2,
                    }}
                  >
                    Choose how Email messages are delivered to your tenants.
                  </div>
                </div>
                <span
                  className="badge badge-info"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {badgeLabel(settings.emailSource)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 0,
                  border: "0.5px solid var(--border-mid)",
                  borderRadius: "var(--radius-md)",
                  overflow: "hidden",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    padding: "12px 14px",
                    background:
                      settings.emailSource === "SaasApi"
                        ? "var(--blue-light)"
                        : "var(--bg-secondary)",
                    borderRight: "0.5px solid var(--border-mid)",
                    cursor: "pointer",
                    opacity: saving.email ? 0.6 : 1,
                  }}
                  onClick={() => handleSourceChange("email", "SaasApi")}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="src-email"
                      checked={settings.emailSource === "SaasApi"}
                      onChange={() => handleSourceChange("email", "SaasApi")}
                    />
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: "var(--blue)",
                      }}
                    >
                      Use SaaS Admin's Email API
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      lineHeight: "1.6",
                    }}
                  >
                    ✓ Pre-configured &amp; deliverable on day one
                    <br />
                    ✓ 1,000 Email included with Growth Plan
                    <br />✓ ₹0.10 per email after limit
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    padding: "12px 14px",
                    background:
                      settings.emailSource === "OwnApi"
                        ? "var(--blue-light)"
                        : "var(--bg-secondary)",
                    cursor: "pointer",
                    opacity: saving.email ? 0.6 : 1,
                  }}
                  onClick={() => handleSourceChange("email", "OwnApi")}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="src-email"
                      checked={settings.emailSource === "OwnApi"}
                      onChange={() => handleSourceChange("email", "OwnApi")}
                    />
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: "var(--text-primary)",
                      }}
                    >
                      Use My Own Email API
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      lineHeight: "1.6",
                    }}
                  >
                    ✓ No SaaS per-message billing
                    <br />
                    ✓ Bring your own gateway credentials
                    <br />⚙{" "}
                    <span
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        color: "var(--blue)",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Configure Email Setting →
                    </span>
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--text-tertiary)",
                  background: "var(--bg-secondary)",
                  padding: "8px 12px",
                  borderRadius: "var(--radius-md)",
                }}
              >
                💡 Switch to Own API if you have a high-volume gateway contract
                that beats ₹0.10 per email.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
